import { ChangeDetectorRef, Component, inject, signal, OnInit, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Loader } from '../../loader/loader';
import { MatIconModule } from '@angular/material/icon';
import { SnackbarService } from '../../services/snackbar.service';
import { ValidationMessagesTexts } from '../../constants/validation-messages.constants';
import { ReportV2Service, RecipeDefinition, UserInputVariable } from '../../services/report-v2.service';
import { Labels } from '../../constants/labels-constant';
import { DatePickerComponent } from '../../components/date-picker/date-picker';
import { DialogueBox } from '../../dialogue-box/dialogue-box';
import { FileProvisioning } from '../file-provisioning/file-provisioning';

@Component({
  selector: 'app-generate-report',
  standalone: true,
  imports: [FormsModule, CommonModule, ReactiveFormsModule, Loader, MatIconModule, DatePickerComponent, DialogueBox, FileProvisioning],
  templateUrl: './generate-report.html',
  styleUrl: './generate-report.scss',
})
export class GenerateReport implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cd = inject(ChangeDetectorRef);
  private reportV2Service = inject(ReportV2Service);
  private snackbar = inject(SnackbarService);

  isLoading = false;
  step = signal(1);
  validationMessages = ValidationMessagesTexts;
  labels = Labels;

  recipeId: number | null = null;
  recipeDefinition = signal<RecipeDefinition | null>(null);
  dynamicForm!: FormGroup;
  variables = signal<UserInputVariable[]>([]);
  isDialogOpen = false;
  dialogMessage = '';
  // F8 generation gate: Generate stays blocked until the file panel reports every
  // required category satisfied (emitted via generationAllowedChange).
  filesReady = signal(false);
  /** uploadId returned by file upload endpoint, passed through createReport payload. */
  uploadedFilesUploadId = signal<string | null>(null);

  fixedForm: FormGroup = this.fb.group({
    reportName: ['', [Validators.required, Validators.maxLength(100)]],
    projectId: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(8), Validators.pattern(/^\d{8}$/)]],
  });

  /** Project ID for the file panel — only once it's a valid 8-digit id, so partial
   *  keystrokes don't trigger manifest loads for wrong ids. */
  get projectIdForFiles(): number | undefined {
    const ctrl = this.fixedForm.get('projectId');
    return ctrl?.valid ? Number(ctrl.value) : undefined;
  }

  /** F8 gate: block Generate on form validity AND the file panel being ready. */
  get generateDisabled(): boolean {
    return this.fixedForm.invalid || this.dynamicForm?.invalid || !this.filesReady();
  }

  ngOnInit(): void {
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    const recipeIdParam = this.route.snapshot.queryParamMap.get('recipeId');
    if (recipeIdParam) {
      this.recipeId = Number(recipeIdParam);
      this.loadRecipeDefinition(this.recipeId);
    } else {
      this.router.navigate(['/report-types']);
    }
  }

  ngOnDestroy(): void {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  private loadRecipeDefinition(recipeId: number): void {
    this.isLoading = true;
    this.reportV2Service.getRecipeDefinition(recipeId).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success && response.data) {
          this.recipeDefinition.set(response.data);
          const sortedVars = [...response.data.userInputVariables].sort(
            (a, b) => a.sortOrder - b.sortOrder
          );
          this.variables.set(sortedVars);
          this.buildDynamicForm(sortedVars);
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response.message || 'Failed to load recipe definition.',
          });
        }
        this.cd.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
        this.cd.markForCheck();
      },
    });
  }

  private buildDynamicForm(variables: UserInputVariable[]): void {
    const group: Record<string, FormControl> = {};

    for (const variable of variables) {
      const validators = [];

      if (variable.isRequired) {
        validators.push(Validators.required);
      }

      if (variable.validation?.minLength) {
        validators.push(Validators.minLength(variable.validation.minLength));
      }
      if (variable.validation?.maxLength) {
        validators.push(Validators.maxLength(variable.validation.maxLength));
      }
      if (variable.validation?.regex) {
        validators.push(Validators.pattern(variable.validation.regex));
      }

      let defaultValue: any = variable.defaultValue ?? '';

      if (variable.type === 'Boolean') {
        defaultValue = variable.defaultValue === 'true';
      } else if (variable.type === 'Number') {
        defaultValue = variable.defaultValue ? Number(variable.defaultValue) : null;
      } else if (variable.type === 'MultiSelection') {
        try {
          defaultValue = variable.defaultValue ? JSON.parse(variable.defaultValue) : [];
        } catch {
          defaultValue = [];
        }
      }

      group[variable.name] = new FormControl(defaultValue, validators);
    }

    this.dynamicForm = this.fb.group(group);
  }

  getControl(name: string): FormControl {
    return this.dynamicForm.get(name) as FormControl;
  }

  toggleMultiSelection(variableName: string, option: string): void {
    const control = this.getControl(variableName);
    const current: string[] = control.value || [];
    const index = current.indexOf(option);
    if (index > -1) {
      control.setValue(current.filter((v) => v !== option));
    } else {
      control.setValue([...current, option]);
    }
    control.markAsTouched();
  }

  isOptionSelected(variableName: string, option: string): boolean {
    const control = this.getControl(variableName);
    const current: string[] = control.value || [];
    return current.includes(option);
  }

  onSubmit(): void {
    this.fixedForm.markAllAsTouched();
    this.dynamicForm.markAllAsTouched();
    if (this.generateDisabled) {
      return;
    }

    const fixedValues = this.fixedForm.value;
    const recipe = this.recipeDefinition();
    const uploadId = this.uploadedFilesUploadId();

    if (!uploadId) {
      this.snackbar.showCustom({
        type: 'error',
        title: this.validationMessages.TOASTER.ERROR.TITLE,
        message: this.validationMessages.CREATE_GENERATE_REPORT.UPLOAD_SOURCE_FILES,
      });
      return;
    }

    const request = {
      reportName: fixedValues.reportName,
      clientId: recipe!.clientId,
      reportTypeId: recipe!.reportTypeId,
      projectId: Number(fixedValues.projectId),
      recipeId: this.recipeId!,
      userInput: this.dynamicForm.value,
      generateReport: false,
      uploadId,
    };

    this.isLoading = true;
    this.reportV2Service.createReport(request).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success) {
          this.dialogMessage = this.labels.GENERATE_REPORT.SUCCESS_MESSAGE;
          this.isDialogOpen = true;
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response.message,
          });
        }
        this.cd.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
        this.cd.markForCheck();
      },
    });
  }

  onDialogClose(): void {
    this.isDialogOpen = false;
    this.router.navigate(['/reports']);
  }

  onCancel(): void {
    this.router.navigate(['/report-types']);
  }

  onUploadIdChange(uploadId: string | null): void {
    this.uploadedFilesUploadId.set(uploadId);
  }
}
